import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from "react-redux";
import { 
    Input, 
    Select,
    Checkbox, 
    Button, 
    Tooltip, 
    Popover,
    message 
} from 'antd';
import { 
    SearchOutlined, 
    PlusOutlined, 
    FolderOutlined,
    InfoCircleOutlined,
    CloseOutlined,
    DownOutlined,
    EditOutlined,
    FileTextOutlined,
    UploadOutlined,
    LoadingOutlined
} from '@ant-design/icons';
import {
    toggleCriterionSelection,
    updateCriterionRefs,
    addCriterionToList,
    updateCriterionInList,
    addReferenceFileToStage
} from "../../../Actions/stage.action";
import { RequestCreateCriterion, RequestUpdateCriterion } from "../../../Actions/critere.action";
import { upload_document } from "../../../Services/referentiel.service";
import CriteriaSidebarSkeleton from "./CriteriaSidebarSkeleton";
import CriterionFormModal from "../../critères/CriterionFormModal";
import { useCanEditCriteria, useCanCreateCriteria } from "../../../utils/roles";
import { useTranslation } from "react-i18next";
import {
    getSelectionReferenceIds,
    isReferenceComparisonCriterion,
    isReferenceReviewCriterion,
} from "../../../utils/comparisonRisk";
import { resolveCriterionText } from "../../../utils/criterionTranslations";
import { findGlobalAnalysisCriterion, isGlobalAnalysisCriterion } from "../../../utils/globalAnalysisCriterion";
import {
    buildCriteriaSelectionDebugEntries,
    getStandardCriteriaSelections,
    normalizeCriteriaSelectionForAvailableCriteria,
} from "../../../utils/criteriaSelection";
import {
    ALL_REVIEW_PROFILE,
    getCriterionReviewProfiles,
    isCriterionVisibleForReviewProfile,
} from "../../../utils/reviewProfiles";
import FeatureLockBadge from "../commercial/FeatureLockBadge";
import { openCommercialGateModal } from "../../../Actions/commercialControl.action";
import {
    buildAcceptAttributeFromFormats,
    buildLockedCommercialDecision,
    canEditCriteriaPrompts,
    canGenerateCriteriaPrompts,
    canManageCriteria,
    canUseRiskComparisonWithUsage,
    canUseSupportingDocumentsWithUsage,
    canViewSystemPrompts,
    getAllowedUploadFormats,
    getMaxCriteriaPerAnalysis,
    getUsageByTypeFromSnapshot,
} from "../../../utils/commercialCapabilities";
import './criteria.css';
import VersionComparisonSelector from './VersionComparisonSelector';

const isSystemCriterion = (criterion) => (
    criterion?.visibility_scope === 'SYSTEM'
    || criterion?.client_id === null
    || criterion?.client_id === undefined
);

const requiresSingleReference = (criterion) => (
    isReferenceComparisonCriterion(criterion)
    || isReferenceReviewCriterion(criterion)
);

function CriteriaCardView({
    criteria,
    templates,
    selectedTemplate,
    selectedCriteria = [],
    onTemplateChange,
    onToggleSelection,
    openModalTrigger = 0,
    animationCycle = 0,
    activeReviewProfile = ALL_REVIEW_PROFILE,
    povTransitioning = false,
    povTransitionPartyName = '',
    comparison,
}) {
    const { t, i18n } = useTranslation("criteria");
    const language = i18n.resolvedLanguage || i18n.language;
    const [searchTerm, setSearchTerm] = useState('');
    const [activeFilter, setActiveFilter] = useState('all');
    const [activeLibrary, setActiveLibrary] = useState('verdictia');
    const [searchExpanded, setSearchExpanded] = useState(false);
    const [templateExpanded, setTemplateExpanded] = useState(false);
    const [contextVisibility, setContextVisibility] = useState({});
    
    // États pour le modal de critère
    const [criterionModalOpen, setCriterionModalOpen] = useState(false);
    const [editingCriterion, setEditingCriterion] = useState(null);
    
    // Ref pour tracker la valeur précédente du trigger (évite l'ouverture auto au montage)
    const prevTriggerRef = useRef(openModalTrigger);

    const dispatch = useDispatch();
    const stage = useSelector((state) => state.stage);
    const documentVersion = useSelector((state) => state.documentVersion);
    const currentUser = useSelector((state) => state.users?.currentUser || state.users?.user || {});
    const isGlobalLoading = useSelector((state) => state.stage.isGlobalLoading);
    const referenceFiles = useSelector((state) => state.stage.reference_file);
    const commercialControl = useSelector((state) => state.commercialControl || {});
    const commercialCapabilities = commercialControl.capabilities || {};
    const reduxUsageByType = commercialControl.usageByType || {};
    const commercialUsageByType = Object.keys(reduxUsageByType).length
        ? reduxUsageByType
        : getUsageByTypeFromSnapshot(commercialControl.usageSnapshot);
    const isSuperAdmin = String(currentUser?.role || "").toUpperCase() === "SUPER_ADMIN";
    const criteriaManagementAllowed = isSuperAdmin || canManageCriteria(commercialCapabilities);
    const canShowSystemPrompts = canViewSystemPrompts(commercialCapabilities, currentUser);
    const promptEditingAllowed = canEditCriteriaPrompts(commercialCapabilities);
    const promptGenerationAllowed = canGenerateCriteriaPrompts(commercialCapabilities);
    const maxCriteriaPerAnalysis = getMaxCriteriaPerAnalysis(commercialCapabilities);
    const allowedUploadFormats = getAllowedUploadFormats(commercialCapabilities);
    const supportingDocumentsAccept = buildAcceptAttributeFromFormats(allowedUploadFormats) || ".pdf,.docx,.txt";
    const selectionLocked = Boolean(documentVersion?.ia_streaming);
    
    // Hooks de droits
    const canEditCriteria = useCanEditCriteria();
    const canCreate = useCanCreateCriteria();

    const openLockedFeature = useCallback((featureKey, overrides = {}) => {
        dispatch(openCommercialGateModal(buildLockedCommercialDecision(featureKey, overrides)));
    }, [dispatch]);

    const getCriterionScope = useCallback((criterion) => {
        if (criterion?.visibility_scope) return criterion.visibility_scope;
        return criterion?.client_id == null ? 'SYSTEM' : 'CLIENT_SHARED';
    }, []);

    const isVerdictiaCriterion = useCallback(
        (criterion) => getCriterionScope(criterion) === 'SYSTEM',
        [getCriterionScope]
    );
    const verdictiaCriteria = useMemo(
        () => (criteria || []).filter(isVerdictiaCriterion),
        [criteria, isVerdictiaCriterion]
    );
    const visibleVerdictiaCriteria = useMemo(
        () => verdictiaCriteria.filter(
            (criterion) => isCriterionVisibleForReviewProfile(criterion, activeReviewProfile)
        ),
        [verdictiaCriteria, activeReviewProfile]
    );
    const activeReviewProfileIsFiltered = activeReviewProfile !== ALL_REVIEW_PROFILE;

    const globalAnalysisCriterion = useMemo(
        () => findGlobalAnalysisCriterion(criteria || []),
        [criteria]
    );
    const normalizedVisibleSelectedCriteria = useMemo(
        () => normalizeCriteriaSelectionForAvailableCriteria(selectedCriteria, criteria),
        [selectedCriteria, criteria]
    );
    const selectedCriteriaDebugEntries = useMemo(
        () => buildCriteriaSelectionDebugEntries(normalizedVisibleSelectedCriteria, i18n.resolvedLanguage || i18n.language),
        [normalizedVisibleSelectedCriteria, i18n.resolvedLanguage, i18n.language]
    );
    const standardSelectedCriteriaCount = useMemo(
        () => getStandardCriteriaSelections(normalizedVisibleSelectedCriteria).length,
        [normalizedVisibleSelectedCriteria]
    );
    const globalAnalysisSelected = Boolean(
        globalAnalysisCriterion
        && normalizedVisibleSelectedCriteria.some(selection => selection.id === globalAnalysisCriterion.id)
    );
    const referenceComparisonSelected = useMemo(
        () => normalizedVisibleSelectedCriteria.some((selection) => {
            const criterion = (criteria || []).find((item) => item.id === selection.id);
            return isReferenceComparisonCriterion(criterion);
        }),
        [normalizedVisibleSelectedCriteria, criteria]
    );

    // Effet pour ouvrir le modal depuis l'extérieur (collapsed sidebar)
    // Ne réagit qu'aux VRAIS changements, pas au montage initial
    useEffect(() => {
        if (openModalTrigger > 0 && openModalTrigger !== prevTriggerRef.current) {
            handleOpenCreateModal();
        }
        prevTriggerRef.current = openModalTrigger;
    }, [openModalTrigger]);

    // Sections existantes pour le formulaire
    const existingSections = useMemo(() => {
        return [...new Set(criteria.map(c => c.section).filter(Boolean))];
    }, [criteria]);

    // Options de contexte pour les documents de référence
    const contextOptions = useMemo(() => {
        if (!referenceFiles || !Array.isArray(referenceFiles)) return [];
        return referenceFiles
            .filter(item => item.is_active)
            .map(item => ({
                value: item.id,
                label: item.title,
                folder: item.folder
            }));
    }, [referenceFiles]);

    // Gestion du contexte
    const handleContextChange = useCallback((itemId, values) => {
        const criterion = criteria.find((item) => item.id === itemId);
        const singleReferenceRequired = requiresSingleReference(criterion);
        const refs = Array.isArray(values) ? values.filter(Boolean) : [values].filter(Boolean);
        const nextRefs = singleReferenceRequired ? refs.slice(-1) : refs;

        if (!selectedCriteria.some(crit => crit.id === itemId)) {
            dispatch(toggleCriterionSelection(itemId));
        }
        dispatch(updateCriterionRefs(itemId, nextRefs));
        message.success(t("messages.contextUpdated"));
    }, [criteria, selectedCriteria, dispatch, t]);

    // Toggle visibilité du sélecteur de contexte
    const toggleContextVisibility = useCallback((itemId) => {
        setContextVisibility(prev => ({
            ...prev,
            [itemId]: !prev[itemId]
        }));
    }, []);

    // Gestion de l'upload d'un fichier depuis le sélecteur de documents
    const handleFileUploaded = useCallback((uploadedFile, criterionId) => {
        // 1. Ajouter le fichier aux reference_files du stage
        dispatch(addReferenceFileToStage(uploadedFile));
        
        // 2. Sélectionner automatiquement ce fichier pour ce critère
        const criterion = criteria.find((item) => item.id === criterionId);
        const singleReferenceRequired = requiresSingleReference(criterion);
        const currentRefs = selectedCriteria.find(c => c.id === criterionId)?.ref || [];
        const newRefs = singleReferenceRequired
            ? [uploadedFile.id]
            : Array.from(new Set([...currentRefs, uploadedFile.id]));
        
        // 3. S'assurer que le critère est sélectionné
        if (!selectedCriteria.some(crit => crit.id === criterionId)) {
            dispatch(toggleCriterionSelection(criterionId));
        }
        
        // 4. Mettre à jour les refs
        dispatch(updateCriterionRefs(criterionId, newRefs));
    }, [criteria, dispatch, selectedCriteria]);

    // Ouvrir le modal pour créer un nouveau critère
    const handleOpenCreateModal = useCallback(() => {
        setEditingCriterion(null);
        setCriterionModalOpen(true);
    }, []);

    // Ouvrir le modal pour modifier un critère existant
    const handleOpenEditModal = useCallback((criterion) => {
        setEditingCriterion({
            section: criterion.section,
            title: criterion.title,
            description: criterion.description,
            translations: criterion.translations,
            prompt: criterion.prompt,
            is_active: criterion.is_active ?? true,
            accept_document: criterion.accept_document ?? false,
            visibility_scope: getCriterionScope(criterion),
            review_profiles: getCriterionReviewProfiles(criterion),
            _id: criterion.id // Stocker l'ID pour la mise à jour
        });
        setCriterionModalOpen(true);
    }, [getCriterionScope]);

    // Soumettre le formulaire (création ou modification)
    const handleCriterionSubmit = useCallback(async (values) => {
        let result;
        
        if (editingCriterion?._id) {
            // Modification
            result = await dispatch(RequestUpdateCriterion(editingCriterion._id, values));
            if (result?.success && result?.data) {
                // Mettre à jour dans la liste du stage
                dispatch(updateCriterionInList(result.data));
            }
        } else {
            // Création
            result = await dispatch(RequestCreateCriterion(values));
            if (result?.success && result?.data) {
                // Ajouter à la liste du stage
                dispatch(addCriterionToList(result.data));
            }
        }
        
        if (result?.success) {
            setCriterionModalOpen(false);
            setEditingCriterion(null);
        }
    }, [editingCriterion, dispatch]);

    // Filtrage des critères
    const filteredData = useMemo(() => {
        let filtered = criteria;

        if (activeFilter !== "selected") {
            filtered = filtered.filter(item =>
                activeLibrary === 'verdictia'
                    ? isVerdictiaCriterion(item)
                    : !isVerdictiaCriterion(item)
            );
            if (activeLibrary === 'verdictia') {
                filtered = filtered.filter(
                    (item) => isCriterionVisibleForReviewProfile(item, activeReviewProfile)
                );
            }
        }

        // Filtre par recherche
        if (searchTerm) {
            const term = searchTerm.toLowerCase();
            filtered = filtered.filter(item => {
                const text = resolveCriterionText(item, language);
                return (
                    text.title?.toLowerCase().includes(term) ||
                    text.description?.toLowerCase().includes(term) ||
                    text.section?.toLowerCase().includes(term)
                );
            });
        }

        // Filtre par statut
        if (activeFilter === "completed") {
            filtered = filtered.filter(item => item.status?.toLowerCase() === "completed");
        } else if (activeFilter === "selected") {
            filtered = filtered.filter(item =>
                selectedCriteria.some(crit => crit.id === item.id)
            );
        }

        if (contextOptions.length === 0) return filtered;

        return [...filtered].sort((a, b) => {
            const aComparison = isReferenceComparisonCriterion(a) ? 0 : 1;
            const bComparison = isReferenceComparisonCriterion(b) ? 0 : 1;
            return aComparison - bComparison;
        });
    }, [criteria, searchTerm, activeFilter, selectedCriteria, activeLibrary, activeReviewProfile, isVerdictiaCriterion, contextOptions.length, language]);

    // Groupement par sections
    const sections = useMemo(() => {
        const grouping = {};
        filteredData.forEach(item => {
            const text = resolveCriterionText(item, language);
            const isAnalysisMode = isGlobalAnalysisCriterion(item) || isReferenceComparisonCriterion(item);
            const section = isAnalysisMode
                ? (String(language || '').toLowerCase().startsWith('fr') ? 'Modes d’analyse' : 'Analysis Modes')
                : (text.section || t("sections.none"));
            const sectionName = activeFilter === "selected"
                ? `${isVerdictiaCriterion(item) ? 'Verdictia' : 'Mes critères'} · ${section}`
                : section;
            grouping[sectionName] = grouping[sectionName] || [];
            grouping[sectionName].push(item);
        });
        return grouping;
    }, [filteredData, activeFilter, isVerdictiaCriterion, language, t]);

    // Stats
    const stats = useMemo(() => ({
        total: activeLibrary === 'verdictia'
            ? (activeReviewProfileIsFiltered ? `${visibleVerdictiaCriteria.length}/${verdictiaCriteria.length}` : verdictiaCriteria.length)
            : criteria.filter(c => !isVerdictiaCriterion(c)).length,
        completed: criteria.filter(c =>
            (activeLibrary === 'verdictia' ? isVerdictiaCriterion(c) : !isVerdictiaCriterion(c))
            && (activeLibrary !== 'verdictia' || isCriterionVisibleForReviewProfile(c, activeReviewProfile))
            && c.status?.toLowerCase() === 'completed'
        ).length,
        selected: selectedCriteria.length
    }), [criteria, selectedCriteria, activeLibrary, activeReviewProfile, activeReviewProfileIsFiltered, visibleVerdictiaCriteria.length, verdictiaCriteria.length, isVerdictiaCriterion]);

    const libraryStats = useMemo(() => ({
        verdictia: verdictiaCriteria.length,
        mine: criteria.filter(c => !isVerdictiaCriterion(c)).length,
    }), [criteria, verdictiaCriteria.length]);

    if (isGlobalLoading) {
        return <CriteriaSidebarSkeleton />;
    }

    return (
        <div className="criteria-sidebar">
            {/* Mini Header - Ultra compact */}
            <div className="criteria-mini-header">
                {/* Tabs de filtrage */}
                <div className="criteria-tabs" role="tablist" aria-label={t("filters.active")}>
                    <button 
                        className={`criteria-tab ${activeFilter === 'all' ? 'active' : ''}`}
                        onClick={() => setActiveFilter('all')}
                        aria-pressed={activeFilter === 'all'}
                    >
                        {t("filters.total")} <span className="criteria-tab__count">{stats.total}</span>
                    </button>
                    <button 
                        className={`criteria-tab ${activeFilter === 'completed' ? 'active' : ''}`}
                        onClick={() => setActiveFilter('completed')}
                        aria-pressed={activeFilter === 'completed'}
                    >
                        ✓ <span className="criteria-tab__count">{stats.completed}</span>
                    </button>
                    <button 
                        className={`criteria-tab ${activeFilter === 'selected' ? 'active' : ''}`}
                        onClick={() => setActiveFilter('selected')}
                        aria-pressed={activeFilter === 'selected'}
                    >
                        ● <span className="criteria-tab__count">{stats.selected}</span>
                    </button>
                </div>

                {/* Toggles recherche + template */}
                <div className="criteria-toggles">
                    {/* Toggle template */}
                    {templates?.length > 0 && (
                        <button 
                            className={`criteria-search-toggle ${templateExpanded ? 'active' : ''}`}
                            onClick={() => {
                                setTemplateExpanded(!templateExpanded);
                                if (!templateExpanded) setSearchExpanded(false);
                            }}
                            title={t("template.placeholder")}
                            aria-label={t("template.placeholder")}
                            aria-pressed={templateExpanded}
                        >
                            <FileTextOutlined />
                        </button>
                    )}
                    
                    {/* Toggle recherche */}
                    <button 
                        className={`criteria-search-toggle ${searchExpanded ? 'active' : ''}`}
                        onClick={() => {
                            setSearchExpanded(!searchExpanded);
                            if (!searchExpanded) setTemplateExpanded(false);
                        }}
                        aria-label={t("search.placeholder")}
                        aria-pressed={searchExpanded}
                    >
                        <SearchOutlined />
                    </button>
                </div>
            </div>

            {povTransitioning && (
                <div className="criteria-pov-transition" role="status" aria-live="polite">
                    <LoadingOutlined spin aria-hidden="true" />
                    <span>{t("povTransition.loading", {
                        partyName: povTransitionPartyName,
                        defaultValue: povTransitionPartyName
                            ? `Mise à jour des critères pour ${povTransitionPartyName}…`
                            : "Mise à jour des critères pour la partie sélectionnée…",
                    })}</span>
                </div>
            )}

            <div className="criteria-library-tabs" role="tablist" aria-label={t("library.aria")}>
                <button
                    className={`criteria-library-tab ${activeLibrary === 'verdictia' ? 'active' : ''}`}
                    onClick={() => setActiveLibrary('verdictia')}
                    aria-pressed={activeLibrary === 'verdictia'}
                >
                    {t("library.verdictia")} <span>{libraryStats.verdictia}</span>
                </button>
                <button
                    className={`criteria-library-tab ${activeLibrary === 'mine' ? 'active' : ''}`}
                    onClick={() => setActiveLibrary('mine')}
                    aria-pressed={activeLibrary === 'mine'}
                >
                    {t("library.mine")} <span>{libraryStats.mine}</span>
                </button>
            </div>

            {globalAnalysisSelected && (
                <div className="criteria-global-exclusive-note">
                    <InfoCircleOutlined />
                    <span>
                        {t("globalExclusive.notice", {
                            defaultValue: "Analyse globale couvre tout le document. Un critère ciblé la remplacera.",
                        })}
                    </span>
                </div>
            )}
            {referenceComparisonSelected && (
                <div className="criteria-global-exclusive-note">
                    <InfoCircleOutlined />
                    <span>
                        {t("referenceComparisonExclusive.notice", {
                            defaultValue: "La comparaison orientée risque s'exécute seule afin de comparer le contrat reçu avec une version de référence.",
                        })}
                    </span>
                </div>
            )}

            {/* Template Selector - expandable */}
            {templateExpanded && templates?.length > 0 && (
                <div className="criteria-template-select">
                    <Select
                        value={selectedTemplate?.id}
                        onChange={onTemplateChange}
                        placeholder={t("template.placeholder")}
                        options={templates.map(tpl => ({
                            value: tpl.id,
                            label: tpl.title
                        }))}
                        allowClear
                        suffixIcon={<FileTextOutlined style={{ color: '#64748b' }} />}
                        autoFocus
                    />
                </div>
            )}

            {/* Barre de recherche expandable */}
            {searchExpanded && (
                <div className="criteria-search-bar">
                    <Input
                        placeholder={t("search.placeholder")}
                        prefix={<SearchOutlined style={{ color: '#94a3b8' }} />}
                        suffix={
                            searchTerm && (
                                <CloseOutlined 
                                    style={{ color: '#94a3b8', cursor: 'pointer' }} 
                                    onClick={() => setSearchTerm('')}
                                />
                            )
                        }
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        autoFocus
                    />
                </div>
            )}

            {/* Quick Add - Bouton d'ajout de critère VISIBLE */}
            {/* Modal de création/modification de critère */}
            <CriterionFormModal
                open={criterionModalOpen}
                onClose={() => {
                    setCriterionModalOpen(false);
                    setEditingCriterion(null);
                }}
                onSubmit={handleCriterionSubmit}
                initialValues={editingCriterion}
                existingSections={existingSections}
                canEditPrompt={promptEditingAllowed}
                canGeneratePrompt={promptGenerationAllowed}
            />

            {/* Liste des critères */}
            <div className="criteria-list">
                {filteredData.length === 0 ? (
                    <div className="criteria-empty">
                        <div className="criteria-empty__icon">📋</div>
                        <div className="criteria-empty__title">
                            {searchTerm ? t("empty.searchTitle") : t("empty.defaultTitle")}
                        </div>
                        <div className="criteria-empty__desc">
                            {searchTerm 
                                ? t("empty.searchDesc", { searchTerm }) 
                                : t("empty.defaultDesc")
                            }
                        </div>
                    </div>
                ) : (
                    Object.entries(sections).map(([sectionName, items]) => (
                        <div key={sectionName} className="section-wrapper">
                            {/* Section Header - discret */}
                            <div className="section-ribbon">
                                <FolderOutlined className="section-ribbon__icon" aria-hidden="true" />
                                <span className="section-ribbon__title">{sectionName}</span>
                                <span
                                    className="section-ribbon__count"
                                    data-count={items.length}
                                    aria-hidden="true"
                                />
                            </div>

                            {/* Critères groupés dans un conteneur */}
                            <div className="section-group">
                                {items.map((item, index) => (
                                    <CriterionItem
                                        key={item.id}
                                        item={item}
                                        comparison={comparison}
                                        comparisonMulti={selectedCriteria.length > 1}
                                        isLast={index === items.length - 1}
                                        selected={selectedCriteria.some(c => c.id === item.id)}
                                        selectionLocked={selectionLocked}
                                        contextOptions={contextOptions}
                                        contextVisible={contextVisibility[item.id]}
                                        itemContexts={selectedCriteria.find(c => c.id === item.id)?.ref || []}
                                        toggleContextVisibility={toggleContextVisibility}
                                        onContextChange={handleContextChange}
                                        onFileUploaded={handleFileUploaded}
                                        onToggleSelection={onToggleSelection}
                                        onEdit={handleOpenEditModal}
                                        canEdit={
                                            criteriaManagementAllowed
                                            && canEditCriteria(item)
                                            && (currentUser?.role === 'SUPER_ADMIN' || !isSystemCriterion(item))
                                        }
                                        criteriaManagementAllowed={criteriaManagementAllowed}
                                        promptEditingAllowed={promptEditingAllowed}
                                        canShowSystemPrompts={canShowSystemPrompts}
                                        commercialCapabilities={commercialCapabilities}
                                        commercialUsageByType={commercialUsageByType}
                                        maxCriteriaPerAnalysis={maxCriteriaPerAnalysis}
                                        selectedCriteriaCount={standardSelectedCriteriaCount}
                                        selectedCriteriaDebugEntries={selectedCriteriaDebugEntries}
                                        globalAnalysisSelected={globalAnalysisSelected}
                                        supportingDocumentsAccept={supportingDocumentsAccept}
                                        onCommercialLocked={openLockedFeature}
                                        language={language}
                                        t={t}
                                    />
                                ))}
                            </div>
                        </div>
                    ))
                )}
            </div>

            {/* Action de création séparée des filtres pour éviter l'effet "troisième tab". */}
            {canCreate && criteriaManagementAllowed && (
                <div className="criteria-action-footer">
                    <Button
                        type="default"
                        icon={<PlusOutlined />}
                        onClick={handleOpenCreateModal}
                        className="criteria-action-footer__button"
                    >
                        {t("quickAdd.trigger")}
                    </Button>
                </div>
            )}
            {canCreate && !criteriaManagementAllowed && (
                <div className="criteria-action-footer">
                    <FeatureLockBadge
                        locked
                        size="medium"
                        label={t("commercial.lock.criteriaManagement", { defaultValue: "Creation de criteres verrouillee" })}
                        tooltip={t("commercial.lock.criteriaManagementTooltip", { defaultValue: "La gestion avancee des criteres depend du plan actif." })}
                        ctaLabel={t("commercial.lock.viewOffers", { defaultValue: "Voir les offres" })}
                        showCtaInline
                        onLockedClick={() => openLockedFeature("criteria_management")}
                    />
                </div>
            )}
        </div>
    );
}

// Composant DocumentSelector - Select avec bouton toggle et upload intégré
const DocumentSelector = ({
    item,
    itemId,
    itemContexts,
    contextOptions,
    contextVisible,
    toggleContextVisibility,
    onContextChange,
    onFileUploaded,
    locked = false,
    onLockedClick,
    accept = ".pdf,.docx,.txt",
    t
}) => {
    const [uploading, setUploading] = useState(false);
    const fileInputRef = useRef(null);
    const singleReferenceRequired = requiresSingleReference(item);
    const selectedReferenceIds = getSelectionReferenceIds({ ref: itemContexts });
    
    const hasOptions = contextOptions && contextOptions.length > 0;
    
    // Gestion de l'upload de fichier
    const handleFileUpload = async (file) => {
        if (!file) return;
        
        setUploading(true);
        try {
            const formData = new FormData();
            formData.append("uploaded_file", file);
            formData.append("title", file.name.replace(/\.[^/.]+$/, "")); // Nom sans extension
            formData.append("folder", t("context.defaultFolderName"));
            formData.append("is_active", "true");
            formData.append("file_usage", "support_document");
            
            const res = await upload_document(formData);
            
            if (res.success && res.data) {
                // Notifier le parent pour ajouter au stage
                if (onFileUploaded) {
                    onFileUploaded(res.data, itemId);
                }
                message.success(t("context.uploadSuccess"));
            } else {
                message.error(res.error || t("context.uploadError"));
            }
        } catch (err) {
            console.error("Upload error:", err);
            message.error(t("context.uploadError"));
        } finally {
            setUploading(false);
            // Reset l'input file
            if (fileInputRef.current) {
                fileInputRef.current.value = "";
            }
        }
    };
    
    const handleInputChange = (e) => {
        const file = e.target.files?.[0];
        if (file) {
            handleFileUpload(file);
        }
    };
    
    const triggerFileInput = () => {
        if (locked) {
            onLockedClick?.();
            return;
        }
        fileInputRef.current?.click();
    };

    if (locked) {
        return (
            <div className="doc-select-wrapper doc-select-wrapper--locked" onClick={e => e.stopPropagation()}>
                <div className="doc-select-empty doc-select-empty--locked">
                    <FeatureLockBadge
                        locked
                        label={t("commercial.lock.availableWithSoloPro", { defaultValue: "Disponible avec Solo / Pro" })}
                        tooltip={t("commercial.lock.supportingDocumentTooltip", { defaultValue: "Les documents d'appui sont verrouilles par le plan actif." })}
                        ctaLabel={t("commercial.lock.viewOffers", { defaultValue: "Voir les offres" })}
                        showCtaInline
                        onLockedClick={onLockedClick}
                    />
                </div>
                <Tooltip title={t("commercial.lock.supportingDocumentTooltip", { defaultValue: "Les documents d'appui sont verrouilles par le plan actif." })}>
                    <button
                        type="button"
                        className="doc-upload-btn"
                        onClick={(e) => { e.stopPropagation(); onLockedClick?.(); }}
                    >
                        <UploadOutlined />
                    </button>
                </Tooltip>
            </div>
        );
    }
    
    return (
        <div className="doc-select-wrapper" onClick={e => e.stopPropagation()}>
            {/* Input file caché */}
            <input
                type="file"
                ref={fileInputRef}
                onChange={handleInputChange}
                style={{ display: 'none' }}
                accept={accept}
            />

            {/* Select — sélection uniquement */}
            <div className="doc-select-container">
                {hasOptions ? (
                    <Select
                        mode={singleReferenceRequired ? undefined : "multiple"}
                        placeholder={singleReferenceRequired
                            ? t("context.comparisonPlaceholder", "Document de reference requis")
                            : t("context.placeholder")}
                        value={singleReferenceRequired ? (selectedReferenceIds[0] || undefined) : itemContexts}
                        onChange={(values) => onContextChange(itemId, values)}
                        options={contextOptions}
                        allowClear
                        size="small"
                        maxTagCount="responsive"
                        className="doc-select"
                        popupClassName="doc-select-dropdown"
                        suffixIcon={
                            <span className="doc-select__arrow-icon" aria-hidden="true">
                                <DownOutlined />
                            </span>
                        }
                        onClick={(e) => e.stopPropagation()}

                    />
                ) : (
                    <div className="doc-select-empty">
                        <span>{t("context.noDocsYet")}</span>
                    </div>
                )}
            </div>

            {/* Bouton upload — action séparée, distincte */}
            <Tooltip title={t("context.uploadNew")}>
                <button
                    type="button"
                    className="doc-upload-btn"
                    onClick={(e) => { e.stopPropagation(); triggerFileInput(); }}
                    disabled={uploading}
                >
                    {uploading ? <LoadingOutlined spin /> : <UploadOutlined />}
                </button>
            </Tooltip>

            {/* Affichage des documents sélectionnés sous forme de tags */}
            {selectedReferenceIds.length > 0 && !hasOptions && (
                <div className="doc-tags-display">
                    {selectedReferenceIds.slice(0, 2).map((contextValue) => {
                        const contextItem = contextOptions.find(opt => opt.value === contextValue);
                        return (
                            <Tooltip key={contextValue} title={contextItem?.label}>
                                <span className="doc-tag">
                                    <FileTextOutlined className="doc-tag__icon" />
                                    {contextItem?.label}
                                </span>
                            </Tooltip>
                        );
                    })}
                    {selectedReferenceIds.length > 2 && (
                        <span className="doc-tag doc-tag--more">+{selectedReferenceIds.length - 2}</span>
                    )}
                </div>
            )}
        </div>
    );
};

// Composant CriterionItem - Élément de critère compact
const CriterionItem = ({ 
    item, 
    comparison,
    comparisonMulti = false,
    isLast,
    selected, 
    selectionLocked = false,
    contextOptions,
    contextVisible,
    itemContexts,
    toggleContextVisibility,
    onContextChange,
    onFileUploaded,
    onToggleSelection,
    onEdit,
    canEdit,
    criteriaManagementAllowed = false,
    promptEditingAllowed = false,
    canShowSystemPrompts = false,
    commercialCapabilities = {},
    commercialUsageByType = {},
    maxCriteriaPerAnalysis = null,
    selectedCriteriaCount = 0,
    selectedCriteriaDebugEntries = [],
    globalAnalysisSelected = false,
    supportingDocumentsAccept = ".pdf,.docx,.txt",
    onCommercialLocked,
    language,
    t 
}) => {
    const isCompleted = item.status?.toLowerCase() === 'completed';
    const isProcessing = item.status?.toLowerCase() === 're_executed';
    const isNotExecuted = item.status?.toLowerCase() === 'not_executed';
    const isComparisonCriterion = isReferenceComparisonCriterion(item);
    const selectionCompleted = isCompleted && !(isComparisonCriterion && comparison?.mode === 'document_version');
    const isReferenceReview = isReferenceReviewCriterion(item);
    const isSingleReferenceCriterion = isComparisonCriterion || isReferenceReview;
    const isSystem = isSystemCriterion(item);
    const isGlobalAnalysis = isGlobalAnalysisCriterion(item);
    const text = resolveCriterionText(item, language);
    const statusKey = isCompleted ? 'completed' : isProcessing ? 'processing' : isNotExecuted ? 'not-executed' : 'pending';
    const supportingDocumentsLocked = item.accept_document
        && !canUseSupportingDocumentsWithUsage(commercialCapabilities, commercialUsageByType);
    const riskComparisonLocked = isComparisonCriterion
        && !canUseRiskComparisonWithUsage(commercialCapabilities, commercialUsageByType);
    const primaryLockFeature = riskComparisonLocked
        ? "risk_comparison"
        : supportingDocumentsLocked
            ? "supporting_document"
            : null;
    const effectiveSelectedCriteriaCount = globalAnalysisSelected && !isGlobalAnalysis
        ? Math.max(0, selectedCriteriaCount - 1)
        : selectedCriteriaCount;
    const selectionLimitReached = !selected
        && !isGlobalAnalysis
        && !isSingleReferenceCriterion
        && maxCriteriaPerAnalysis !== null
        && effectiveSelectedCriteriaCount >= maxCriteriaPerAnalysis;
    const canRevealPrompt = Boolean(item.prompt) && (
        canShowSystemPrompts ||
        (!isSystem && criteriaManagementAllowed && promptEditingAllowed)
    );

    const getStatusClass = () => {
        if (isCompleted) return 'status-badge--completed';
        if (isProcessing) return 'status-badge--processing';
        if (isNotExecuted) return 'status-badge--not-executed';
        return 'status-badge--pending';
    };

    const getStatusIcon = () => {
        if (isCompleted) return '✓';
        if (isProcessing) return '◐';
        if (isNotExecuted) return '○';
        return '○';
    };

    const getStatusTooltip = () => {
        if (isCompleted) return t("status.completedTooltip");
        if (isProcessing) return t("status.processingTooltip");
        if (isNotExecuted) return t("status.notExecutedTooltip");
        return t("status.pendingTooltip");
    };

    const handleClick = () => {
        if (selectionLocked) return;
        if (!selectionCompleted) {
            if (riskComparisonLocked) {
                onCommercialLocked?.("risk_comparison");
                return;
            }
            if (selectionLimitReached) {
                if (process.env.NODE_ENV !== "production") {
                    console.info("criteria_selection_limit_debug", {
                        max_criteria_per_analysis: maxCriteriaPerAnalysis,
                        selected_criteria_count: selectedCriteriaCount,
                        effective_selected_criteria_count: effectiveSelectedCriteriaCount,
                        blocked_criterion: {
                            id: item.id,
                            label: text.title,
                            status: item.status || null,
                            source: item.visibility_scope || (item.client_id == null ? "SYSTEM" : "CLIENT_SHARED"),
                            type: item.criterion_type || null,
                        },
                        counted_criteria: selectedCriteriaDebugEntries,
                    });
                }
                onCommercialLocked?.("max_criteria", {
                    metadata_json: {
                        max_criteria_per_analysis: maxCriteriaPerAnalysis,
                        selected_criteria_count: selectedCriteriaCount,
                    },
                });
                return;
            }
            onToggleSelection(item.id);
        }
    };

    const itemClasses = [
        'criterion-item',
        selected ? 'criterion-item--selected' : '',
        isComparisonCriterion ? 'criterion-item--comparison' : '',
        isCompleted ? 'criterion-item--completed' : '',
        selectionLocked ? 'criterion-item--selection-locked' : '',
        isLast ? 'criterion-item--last' : ''
    ].filter(Boolean).join(' ');

    return (
        <div
            className={itemClasses}
            data-status={statusKey}
            aria-disabled={selectionLocked || selectionCompleted}
            onClick={handleClick}
        >
            {/* Indicateur de sélection - barre latérale */}
            {selected && <div className="criterion-item__selected-bar" />}
            
            {/* Checkbox */}
            <Tooltip 
                title={selectionLocked
                    ? t("status.selectionLockedDuringAnalysis", {
                        defaultValue: "Sélection verrouillée pendant l’analyse",
                    })
                    : selectionCompleted
                        ? t("status.alreadyCompleted")
                        : null}
                placement="top"
            >
                <Checkbox
                    className="criterion-item__checkbox"
                    checked={selected}
                    disabled={selectionCompleted || selectionLocked}
                    onClick={(e) => {
                        e.stopPropagation();
                        handleClick();
                    }}
                />
            </Tooltip>

            {/* Content - titre + sélecteur de documents */}
            <div className="criterion-item__content">
                <div className="criterion-item__title-row">
                    <div className="criterion-item__title-line">
                        <span className="criterion-item__title">{text.title}</span>
                        {/* Indicateur que ce critère accepte des documents */}
                        {item.accept_document && !isCompleted && !selected && (
                            <Tooltip title={t("context.acceptsDoc")} placement="top">
                                <FileTextOutlined className="criterion-item__doc-indicator" />
                            </Tooltip>
                        )}
                        <FeatureLockBadge
                            locked={!!primaryLockFeature}
                            label={t("commercial.lock.availableWithSoloPro", { defaultValue: "Disponible avec Solo / Pro" })}
                            tooltip={
                                primaryLockFeature === "risk_comparison"
                                    ? t("commercial.lock.riskComparisonTooltip", { defaultValue: "La comparaison orientée risque est verrouillée par le plan actif." })
                                    : t("commercial.lock.supportingDocumentTooltip", { defaultValue: "Les documents d'appui sont verrouillés par le plan actif." })
                            }
                            ctaLabel={t("commercial.lock.viewOffers", { defaultValue: "Voir les offres" })}
                            showCtaInline
                            onLockedClick={() => onCommercialLocked?.(primaryLockFeature || "supporting_document")}
                        />
                    </div>
                    
                    {/* Sélecteur de documents - visible uniquement si critère sélectionné ET accepte documents */}
                    {selected && isComparisonCriterion && (
                        <VersionComparisonSelector
                            comparison={{ ...comparison, completed: isCompleted }}
                            disabled={selectionLocked || riskComparisonLocked || comparison?.launching}
                            multi={comparisonMulti}
                            externalSelector={!isCompleted ? <DocumentSelector
                                item={item}
                                itemId={item.id}
                                itemContexts={itemContexts}
                                contextOptions={contextOptions}
                                contextVisible={contextVisible}
                                toggleContextVisibility={toggleContextVisibility}
                                onContextChange={onContextChange}
                                onFileUploaded={onFileUploaded}
                                locked={supportingDocumentsLocked || riskComparisonLocked}
                                onLockedClick={() => onCommercialLocked?.(primaryLockFeature || "supporting_document")}
                                accept={supportingDocumentsAccept}
                                t={t}
                            /> : <p>Comparaison externe déjà exécutée pour cette version.</p>}
                        />
                    )}
                    {selected && !isCompleted && !isComparisonCriterion && item.accept_document && (
                        <DocumentSelector
                            item={item}
                            itemId={item.id}
                            itemContexts={itemContexts}
                            contextOptions={contextOptions}
                            contextVisible={contextVisible}
                            toggleContextVisibility={toggleContextVisibility}
                            onContextChange={onContextChange}
                            onFileUploaded={onFileUploaded}
                            locked={supportingDocumentsLocked || riskComparisonLocked}
                            onLockedClick={() => onCommercialLocked?.(primaryLockFeature || "supporting_document")}
                            accept={supportingDocumentsAccept}
                            t={t}
                        />
                    )}
                </div>
            </div>

            {/* Actions au hover - info button */}
            <div className="criterion-item__actions">
                {/* Bouton d'édition - visible uniquement si l'utilisateur a les droits */}
                {canEdit && (
                    <Tooltip title={t("actions.edit")} placement="top">
                        <Button
                            type="text"
                            size="small"
                            icon={<EditOutlined />}
                            className="criterion-item__action-btn criterion-item__action-btn--edit"
                            onClick={(e) => {
                                e.stopPropagation();
                                onEdit(item);
                            }}
                        />
                    </Tooltip>
                )}
                
                {!isSystem && (
                    <Tooltip title={t("details.viewPrompt")} placement="top">
                        <Popover
                            content={
                                <div className="criterion-popover-content">
                                    <div className="criterion-popover-content__title">{text.title}</div>
                                    {text.description && (
                                        <div className="criterion-popover-content__description">
                                            {text.description}
                                        </div>
                                    )}
                                    {canRevealPrompt && (
                                        <div className="criterion-popover-content__prompt">
                                            {item.prompt}
                                        </div>
                                    )}
                                    {!canRevealPrompt && item.prompt && (
                                        <div className="criterion-popover-content__description">
                                            {t("commercial.lock.promptHidden", { defaultValue: "Prompt IA interne masque." })}
                                        </div>
                                    )}
                                </div>
                            }
                            title={t("details.promptText")}
                            trigger="click"
                            placement="rightTop"
                            align={{ offset: [8, 0] }}
                        >
                            <Button
                                type="text"
                                size="small"
                                icon={<InfoCircleOutlined />}
                                className="criterion-item__action-btn"
                                onClick={(e) => e.stopPropagation()}
                            />
                        </Popover>
                    </Tooltip>
                )}
            </div>

            {/* Status badge - aligné à droite avec tooltip */}
            <Tooltip title={getStatusTooltip()} placement="top">
                <div
                    className={`status-badge ${getStatusClass()}`}
                    aria-label={getStatusTooltip()}
                >
                    <span
                        className="status-badge__decorative"
                        data-icon={getStatusIcon()}
                        aria-hidden="true"
                    />
                </div>
            </Tooltip>
        </div>
    );
};


export default CriteriaCardView;
